import { List, Stack, Text } from '@mantine/core';
import clsx from 'clsx';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import classes from './results-clause-help-content.module.css';

export type ResultsClause = 'new' | 'gp';
export type ResultsClauseHelpVariant = 'summary' | 'full';

const LIST_SPACING = 6;

export interface ResultsClauseHelpContentProps {
  clause: ResultsClause;
  variant: ResultsClauseHelpVariant;
  showClauseLabel?: boolean;
  className?: string;
}

function HelpListItem({
  children,
  color,
}: {
  children: string;
  color?: string;
}) {
  return (
    <List.Item>
      <Text c={color} size="sm" ta="justify">
        {children}
      </Text>
    </List.Item>
  );
}

export const ResultsClauseHelpContent: FC<ResultsClauseHelpContentProps> = ({
  clause,
  variant,
  showClauseLabel = false,
  className,
}) => {
  const { t } = useTranslation();
  const prefix = `sampling.results.help.${clause}`;

  return (
    <Stack className={clsx(classes.root, className)} gap="sm">
      {showClauseLabel ? (
        <Text fw={700} size="sm">
          {t('sampling.results.help.clause_label', {
            clause: t(`clauses.${clause}`),
          })}
        </Text>
      ) : null}

      <Text size="sm" ta="justify">
        {t(`${prefix}.intro`)}
      </Text>
      <Text size="sm" ta="justify">
        {t(`${prefix}.definition`)}
      </Text>

      {variant === 'summary' ? (
        <Text c="dimmed" size="sm" ta="justify">
          {t('sampling.results.help.detail_hint')}
        </Text>
      ) : (
        <>
          <Text className={classes.sectionTitle} fw={600} size="sm">
            {t('sampling.results.help.values_title')}
          </Text>
          <List className={classes.list} size="sm" spacing={LIST_SPACING} withPadding>
            <HelpListItem>{t(`${prefix}.values_item_1`)}</HelpListItem>
            <HelpListItem>{t(`${prefix}.values_item_2`)}</HelpListItem>
          </List>

          <Text size="sm" ta="justify">
            {t(`${prefix}.categories_intro`)}
          </Text>

          <List className={classes.list} size="sm" spacing={LIST_SPACING} withPadding>
            <HelpListItem color="red.7">{t(`${prefix}.risk_red`)}</HelpListItem>
            <HelpListItem color="orange.7">
              {t(`${prefix}.risk_orange`)}
            </HelpListItem>
            <HelpListItem color="green.7">
              {t(`${prefix}.risk_green`)}
            </HelpListItem>
          </List>
        </>
      )}
    </Stack>
  );
};
